import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import {
  CashInfoByCurrency,
  ExchangePeriodHoursType,
  OrderExchangeData,
  OrderExchangeOrderFields,
  SelectedAccount,
  ValidatedOrder,
} from '../../types';
import { isDefined, Loadable, LoadableState, noop, useDebouncedValue } from '../../utils';
import { OpenOrder } from '../open-orders';
import { useAccountBalanceCommon } from '../portfolio';
import { NormalOrderSchemaParams, OrderFormData, useNormalOrderSchemaCommon } from '../schemas';
import {
  useOrderExchangeActiveBalancesCommon,
  UseOrderExchangeActiveBalancesCommonPlatformParams,
} from './useOrderExchangeActiveBalancesCommon';
import {
  useOrderExchangeBalanceForCurrencyCommon,
  UseOrderExchangeBalanceForCurrencyCommonPlatformParams,
} from './useOrderExchangeBalanceForCurrencyCommon';
import { useOrderExchangeConditionsCommon } from './useOrderExchangeConditionsCommon';
import {
  useOrderExchangeExchangeRateCommon,
  UseOrderExchangeExchangeRateCommonPlatformParams,
} from './useOrderExchangeExchangeRateCommon';
import {
  useOrderExchangeFundingCommon,
  UseOrderExchangeFundingCommonPlatformParams,
} from './useOrderExchangeFundingCommon';

export type UseOrderExchangeCommonParams = {
  orderForm: OrderFormData;
  selectedAccount: SelectedAccount | undefined;
  openOrder: OpenOrder | undefined;
  exchangePeriodHours: ExchangePeriodHoursType;
};

export type UseOrderExchangeCommonPlatformParams = NormalOrderSchemaParams &
  UseOrderExchangeActiveBalancesCommonPlatformParams &
  UseOrderExchangeBalanceForCurrencyCommonPlatformParams &
  UseOrderExchangeExchangeRateCommonPlatformParams &
  UseOrderExchangeFundingCommonPlatformParams;

// When the basic order form is edited (limit price, quantity, etc) we debounce requests with this much delay.
const OrderDebounceDelay = 1000;

export const useOrderExchangeCommon = (
  { orderForm, selectedAccount, openOrder, exchangePeriodHours }: UseOrderExchangeCommonParams,
  {
    getTradingSessionOptions,
    accountResource,
    getResourceHeaderWithTokenForAccount,
    orderManagementResource,
  }: UseOrderExchangeCommonPlatformParams,
): OrderExchangeData => {
  const { getNormalOrderSchema } = useNormalOrderSchemaCommon({ getTradingSessionOptions });
  const { getCashInfoByCurrency } = useAccountBalanceCommon();
  const { balanceForCurrency } = useOrderExchangeBalanceForCurrencyCommon({
    accountResource,
    getResourceHeaderWithTokenForAccount,
  });
  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>();
  const [isEnabled, setEnabled] = useState(false);
  const instrument = orderForm?.instrument;

  // A validated order. If it's not valid, we can't compute everything for the exchange.
  const validatedOrder = useMemo<ValidatedOrder | undefined>(() => {
    const schema = getNormalOrderSchema(orderForm);
    return schema.isValidSync(orderForm, { abortEarly: false }) ? (orderForm as ValidatedOrder) : undefined;
  }, [getNormalOrderSchema, orderForm]);

  // To prevent orders changes (amount/price typing, etc) to instantly trigger http requests we debounce the validated order.
  const { debouncedValue: order, isPending } = useDebouncedValue({
    delay: OrderDebounceDelay,
    value: validatedOrder,
    shouldSkipDelay: shouldSkipOrderDebounce,
  });

  const cashInfo = useMemo<LoadableState<CashInfoByCurrency>>(() => {
    return isDefined(selectedAccount) ? getCashInfoByCurrency(selectedAccount) : Loadable.loading;
  }, [getCashInfoByCurrency, selectedAccount]);

  // Unified hook to load and keep up to date the active source currencies and their balances.
  const {
    balances,
    balancesByCurrency,
    individuallyLoadingBalances,
    isCurrencyExchangeActive,
    loadActiveBalances,
    startRefreshActiveBalances,
    stopRefreshActiveBalances,
    resetActiveBalances,
  } = useOrderExchangeActiveBalancesCommon(
    {
      cashInfo,
      instrument,
      selectedAccount,
      exchangePeriodHours,
    },
    { accountResource, getResourceHeaderWithTokenForAccount },
  );

  // Funding options: does the user have enough in the instruments "native" currency? If not, how much is missing?
  const {
    loadFunding,
    setFundingMode,
    reset: resetFunding,
    funding,
    fundingMode,
  } = useOrderExchangeFundingCommon(
    {
      order,
      selectedAccount,
      isExchangeEnabled: isEnabled,
      openOrder,
    },
    { accountResource, orderManagementResource, getResourceHeaderWithTokenForAccount },
  );

  // The exchange rate + settlement date - can only be loaded if funding is loaded, and a source currency has been selected.
  const {
    exchangeRate,
    settlementDate,
    loadExchangeRate,
    reset: resetExchangeRate,
  } = useOrderExchangeExchangeRateCommon(
    {
      funding,
      fundingMode,
      selectedAccount,
      sourceCurrency: selectedCurrency,
      balancesByCurrency,
      instrument,
    },
    { orderManagementResource, getResourceHeaderWithTokenForAccount },
  );

  // Conditions that have to be fulfilled to be able to start an order with exchange.
  const { conditions } = useOrderExchangeConditionsCommon({
    cashInfo,
    selectedAccount,
    orderForm,
    openOrder,
    funding,
  });

  const orderFields = useMemo<LoadableState<OrderExchangeOrderFields>>(() => {
    if (!isEnabled || !isDefined(fundingMode) || !isDefined(selectedCurrency)) {
      return Loadable.uninitialized;
    }
    return Loadable.merge([exchangeRate, funding], (exchangeRate, funding): OrderExchangeOrderFields => {
      const targetAmount = fundingMode === 'fullAmount' ? funding.remaining.total : funding.missingAmount;
      return {
        exchangeRate: exchangeRate.rawExchangeRate,
        currencySourceCurrency: selectedCurrency,
        currencySourceAmount: Math.ceil(targetAmount * exchangeRate.exchangeRate),
        currencyTargetAmount: targetAmount,
      };
    });
  }, [exchangeRate, funding, fundingMode, isEnabled, selectedCurrency]);

  // Callbacks wrapped in effect events. Helps fine tuned loads, instead of "load when any callback dep changes".
  const loadFundingInEffect = useEffectEvent((): void => {
    loadFunding();
  });

  const loadActiveBalancesInEffect = useEffectEvent((): void => {
    loadActiveBalances();
  });

  const startRefreshInEffect = useEffectEvent((): void => {
    startRefreshActiveBalances();
  });

  const stopRefreshInEffect = useEffectEvent((): void => {
    stopRefreshActiveBalances();
  });

  const loadExchangeRateInEffect = useEffectEvent((): void => {
    loadExchangeRate();
  });

  const autoSetSourceCurrency = useEffectEvent((): void => {
    // Nothing to do until balances are settled
    if (!Loadable.hasValue(balances)) {
      return;
    }
    const [firstBalance] = balances;
    if (isDefined(firstBalance) && !isDefined(selectedCurrency) && (firstBalance.investableAmount ?? 0) > 0) {
      setSelectedCurrency(firstBalance.currency);
    }
  });

  const resetExchangeRateInEffect = useEffectEvent((): void => {
    resetExchangeRate();
  });

  const resetFundingInEffect = useEffectEvent((): void => {
    resetFunding();
  });

  const resetInEffect = useEffectEvent((): void => {
    setSelectedCurrency(undefined);
    resetActiveBalances();
    resetExchangeRate();
  });

  // Reset exchange-specific state when the toggle is turned off. Funding is kept, it drives a message in "off" state too.
  useEffect(() => {
    if (!isEnabled) {
      resetInEffect();
    }
  }, [isEnabled]);

  // Start/stop the source currency refresh timer based on whether exchange is enabled.
  useEffect(() => {
    if (!isEnabled) {
      return noop;
    }
    startRefreshInEffect();
    return () => stopRefreshInEffect();
  }, [isEnabled]);

  // Load available source currencies. Do this sparingly, as everything depends on this.
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    loadActiveBalancesInEffect();
  }, [isEnabled, selectedAccount?.account?.accountNumber, cashInfo, instrument?.currency]);

  // Auto set currency when balances change.
  useEffect(() => {
    autoSetSourceCurrency();
  }, [balances]);

  // Always load funding regardless of isEnabled — needed to show the "not enough cash" error in the header.
  useEffect(() => {
    loadFundingInEffect();
  }, [order, cashInfo, selectedAccount?.account?.accountNumber, balanceForCurrency]);

  // Reload the exchange rate and settlement date together when the selected currency, funding result, or account changes.
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    loadExchangeRateInEffect();
  }, [
    isEnabled,
    selectedCurrency,
    funding,
    fundingMode,
    selectedAccount?.account?.accountNumber,
    balances,
    instrument?.mic,
  ]);

  // Reset funding while debounce is pending, so the UI shows loading instead of stale data.
  // Funding is always loaded regardless of isEnabled - needed for conditions/warnings.
  useEffect(() => {
    if (isPending) {
      resetFundingInEffect();
    }
  }, [isPending]);

  // Reset exchange rate while debounce is pending, only when exchange is enabled.
  useEffect(() => {
    if (isPending && isEnabled) {
      resetExchangeRateInEffect();
    }
  }, [isPending, isEnabled]);

  return {
    funding,
    fundingMode,
    balances,
    balancesByCurrency,
    individuallyLoadingBalances,
    selectedCurrency,
    exchangeRate,
    settlementDate,
    isEnabled,
    orderFields,
    conditions,
    isEdit: isDefined(openOrder),
    isCurrencyExchangeActive,
    loadFunding,
    loadBalances: loadActiveBalances,
    loadExchangeRate,
    setFundingMode,
    setSelectedCurrency,
    setEnabled,
  };
};

// When we move from an invalid order to a valid order skip the delay delegate the order immediately
const shouldSkipOrderDebounce = (
  oldOrder: ValidatedOrder | undefined,
  newOrder: ValidatedOrder | undefined,
): boolean => {
  return !isDefined(oldOrder) && isDefined(newOrder);
};
